import {Tabs, Box, Text, HStack, Center, Spinner} from '@chakra-ui/react'
import {
  DispatchMode,
  Batch,
  BatchQuantityError,
  PatientTherapySequence,
  SubstanceBatchTypes,
} from '@cypro/engine/schema'
import {PickingListItemForm} from '../../form/picking-list-item-form'
import {useGetAllProductionLocations} from '@/modules/production/api/use-get-all-production-locations'
import {useTranslate} from '@tolgee/react'
import {withForm} from '@cypro/ui/integrations/form'
import {useStore} from '@tanstack/react-form'
import {FormEvent, useEffect, useMemo} from 'react'
import {FLAGS} from '@/shared/constants'
import {StartProductionFormValues} from '../picking-list-drawer'
import {getRequiredAmountForPrescriptions} from '../../utils'
import {PatientTherapySequenceStatus} from '@cypro/engine/database/schema'
import {Tooltip} from '@cypro/ui/components'

interface ViewByOrderTabProps {
  selectedOrders: PatientTherapySequence[]
  batches: Batch[]
  batchesLoading?: boolean
  batchErrors?: BatchQuantityError[]
  addBatch: (batchId: string) => void
  handleRemoveBatch: (batchId: string, category: SubstanceBatchTypes, orderIndex: number) => void
  handleIsolateBatch: (batchId: string, category: SubstanceBatchTypes, orderIndex: number) => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
  onBlur?: () => void
}

export const ViewByOrderTab = withForm({
  defaultValues: {
    orders: [],
    allUsedBatchIds: [],
    productionLocationId: '',
    dispatchMode: DispatchMode.enum.manual,
  } as StartProductionFormValues,
  props: {} as ViewByOrderTabProps,
  render: function Render({
    form,
    selectedOrders,
    batches,
    batchesLoading,
    addBatch,
    handleRemoveBatch,
    handleIsolateBatch,
    onSubmit,
    batchErrors,
    onBlur,
  }) {
    const {t} = useTranslate('common')
    const [productionLocations] = useGetAllProductionLocations()
    const requiredAmounts = useMemo(() => {
      return getRequiredAmountForPrescriptions(selectedOrders)
    }, [selectedOrders])

    const canSendToProduction = selectedOrders.every(
      o => o.status === PatientTherapySequenceStatus.enum.production_planning
    )

    const dispatchMode = useStore(form.store, state => state.values.dispatchMode)

    const productionLocationItems = productionLocations?.map(loc => ({label: loc.name, value: loc.id})) || []

    useEffect(() => {
      if (productionLocations?.length === 1 && !form.state.values.productionLocationId) {
        form.setFieldValue('productionLocationId', productionLocations[0].id, {
          dontRunListeners: true,
          dontUpdateMeta: true,
        })
      }
    }, [productionLocations, form])

    return (
      <Tabs.Content value="orders" spaceY="3" display="flex" flexDirection="column">
        <Box display="flex" flexDirection="column" pb={1}>
          <Text fontSize="md" fontWeight="medium">
            {t('count_selected_orders', {count: selectedOrders.length})}
          </Text>
          <Text fontSize="sm" fontWeight="normal" color="fg.muted">
            {t('review_orders')}
          </Text>
        </Box>
        <form onSubmit={onSubmit}>
          <form.AppForm>
            <Box display="flex" flexDirection="column" gap="6">
              {batchesLoading ? (
                <Center paddingBlock="6">
                  <Spinner aria-label={t('loading')} />
                </Center>
              ) : (
                <form.Field name="orders" mode="array">
                  {field =>
                    field?.state?.value?.map((_order, i) => {
                      const order = selectedOrders.find(o => o.id === _order.sequenceId)
                      if (!order) return null
                      const requiredAmountForOrder = requiredAmounts[order.id]
                      return (
                        <PickingListItemForm
                          key={order.id}
                          index={i}
                          form={form}
                          fields={`orders[${i}]`}
                          order={order}
                          batches={batches}
                          addBatch={addBatch}
                          onRemoveBatch={(batchId, category) => handleRemoveBatch(batchId, category, i)}
                          onIsolateBatch={(batchId, category) => handleIsolateBatch(batchId, category, i)}
                          batchErrors={batchErrors}
                          requiredAmounts={requiredAmountForOrder}
                          onBlur={onBlur}
                        />
                      )
                    })
                  }
                </form.Field>
              )}

              <Box spaceY="4">
                {FLAGS.smartCompounder && (
                  <form.AppField name="dispatchMode">
                    {field => (
                      <field.RadioCard
                        label={t('production_type')}
                        items={[
                          {label: t('manual_production'), value: DispatchMode.enum.manual},
                          {label: t('smart_compounder'), value: DispatchMode.enum['smart-compounder']},
                        ]}
                        display="grid"
                        gridTemplateColumns="1fr 1fr"
                        gapX="4"
                        gapY="4"
                        disabled={!canSendToProduction}
                      />
                    )}
                  </form.AppField>
                )}

                <HStack>
                  <form.AppField name="productionLocationId" mode="value">
                    {field => (
                      <field.Select
                        label={t('production_location')}
                        maxWidth="16rem"
                        hideLabel
                        placeholder={t('assign_workstation')}
                        items={productionLocationItems}
                        portalled={false}
                        disabled={!canSendToProduction}
                      />
                    )}
                  </form.AppField>

                  <Tooltip content={canSendToProduction ? undefined : t('cannot_send_to_production')}>
                    <form.SubmitButton colorPalette="cyan" disabled={!canSendToProduction || batchesLoading}>
                      {dispatchMode === DispatchMode.enum['smart-compounder']
                        ? t('send_to_smart_compounder')
                        : t('send_to_production')}
                    </form.SubmitButton>
                  </Tooltip>
                </HStack>
              </Box>
            </Box>
          </form.AppForm>
        </form>
      </Tabs.Content>
    )
  },
})
